export const FILE_NAME_MAX_DISPLAY_WIDTH = 30; // roughly 15 full-width chars

type GraphemeSegmenter = {
  segment: (value: string) => Iterable<{ segment: string }>;
};

const intlObject = Intl as unknown as {
  Segmenter?: new (
    locales?: string | string[],
    options?: { granularity?: "grapheme" | "word" | "sentence" },
  ) => GraphemeSegmenter;
};

const graphemeSegmenter =
  typeof intlObject.Segmenter === "function"
    ? new intlObject.Segmenter(undefined, { granularity: "grapheme" })
    : null;

const segmentGraphemes = (text: string): string[] => {
  if (graphemeSegmenter) {
    return Array.from(
      graphemeSegmenter.segment(text),
      (segment) => segment.segment,
    );
  }
  return Array.from(text);
};

const getCodePointDisplayWidth = (codePoint: number): number => {
  // Combining marks and variation selectors do not add visible width by themselves.
  const isZeroWidth =
    (codePoint >= 0x0300 && codePoint <= 0x036f) ||
    (codePoint >= 0x1ab0 && codePoint <= 0x1aff) ||
    (codePoint >= 0x1dc0 && codePoint <= 0x1dff) ||
    (codePoint >= 0x20d0 && codePoint <= 0x20ff) ||
    (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
    (codePoint >= 0xfe20 && codePoint <= 0xfe2f) ||
    (codePoint >= 0xe0100 && codePoint <= 0xe01ef);

  if (isZeroWidth) return 0;

  const isFullWidth =
    (codePoint >= 0x1100 && codePoint <= 0x115f) ||
    (codePoint >= 0x2e80 && codePoint <= 0xa4cf) ||
    (codePoint >= 0xac00 && codePoint <= 0xd7a3) ||
    (codePoint >= 0xf900 && codePoint <= 0xfaff) ||
    (codePoint >= 0xfe10 && codePoint <= 0xfe6f) ||
    (codePoint >= 0xff01 && codePoint <= 0xff60) ||
    (codePoint >= 0xffe0 && codePoint <= 0xffe6);

  return isFullWidth ? 2 : 1;
};

const getGraphemeDisplayWidth = (grapheme: string): number => {
  // Most emoji graphemes occupy two cells in common UI fonts.
  if (/\p{Extended_Pictographic}/u.test(grapheme)) {
    return 2;
  }

  let width = 0;
  for (const char of grapheme) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) continue;
    width += getCodePointDisplayWidth(codePoint);
  }

  return width > 0 ? width : 1;
};

export const truncateFileNameByDisplayWidth = (
  text: string,
  maxDisplayWidth = FILE_NAME_MAX_DISPLAY_WIDTH,
): string => {
  let currentWidth = 0;
  let truncated = "";

  for (const grapheme of segmentGraphemes(text)) {
    const nextWidth = currentWidth + getGraphemeDisplayWidth(grapheme);
    if (nextWidth > maxDisplayWidth) {
      return `${truncated}...`;
    }
    truncated += grapheme;
    currentWidth = nextWidth;
  }

  return text;
};
